import React, { useState, useEffect } from 'react';
import Axios from "axios";
import { 
  Grid, Typography, CircularProgress, Container, Box, 
  Button, Card, CardActionArea, Avatar, Chip, TextField, MenuItem,
  Tabs, Tab, FormControl, InputLabel
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import defaultProfilePicture from "../../Assets/defaultProfilePicture.jpg";
import PeopleIcon from '@mui/icons-material/People';
import { DayLabourerCategories } from "../../Constants/BusinessOptions";
import config, { environment } from '../../config';

function DayLabourers() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [dayLabourers, setDayLabourers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tabValue, setTabValue] = useState(1);

  // Set tab to 0 (Registered) if there are no recommended labourers but there are registered ones
  useEffect(() => {
    if (!loading && dayLabourers.length > 0) {
      const hasRegistered = dayLabourers.some(l => !l.is_recommended);
      const hasRecommended = dayLabourers.some(l => l.is_recommended);
      
      if (!hasRecommended && hasRegistered && tabValue === 1) {
        setTabValue(0);
      }
    }
  }, [loading, dayLabourers, tabValue]);

  // Location filters
  const [szekFilter, setSzekFilter] = useState('');
  const [kozsegFilter, setKozsegFilter] = useState('');
  const [szekOptions, setSzekOptions] = useState([]);
  const [kozsegOptions, setKozsegOptions] = useState([]);
  const [isCsango, setIsCsango] = useState(false);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function fetchDayLabourers() {
      try {
        const response = await Axios.get(`${config[environment].businessesEndpoint}?type=DayLabourer`, { cancelToken: source.token });
        setDayLabourers(response.data);
        setLoading(false);
      } catch (error) {
        if (!Axios.isCancel(error)) {
          setLoading(false);
        }
      }
    }

    async function fetchSzekek() {
      try {
        if (isCsango) {
          setSzekOptions([{ id: 'csango', nev: 'Csángóföld' }]);
        } else {
          const response = await Axios.get(`${config[environment].apiEndpoint}api/szek/`);
          const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
          setSzekOptions(data);
        }
      } catch (e) {
      }
    }

    fetchDayLabourers();
    fetchSzekek();
    return () => source.cancel();
  }, [isCsango]);

  useEffect(() => {
    if (szekFilter || isCsango) {
      const fetchKozsegek = async () => {
        try {
          if (isCsango) {
            const fetchAllKozsegek = async (page = 1, accumulated = []) => {
              const response = await Axios.get(config[environment].csangoKozsegVarosEndpoint(page));
              let data = [];
              if (response.data.features) {
                data = response.data.features.map(f => ({
                  id: f.properties.Id || f.properties.id || f.id,
                  nev: f.properties.név || f.properties.nev || f.properties.name || f.properties.nev_hu
                }));
              } else {
                const results = response.data.results || response.data || [];
                data = (Array.isArray(results) ? results : []).map(item => ({
                  id: item.id || item.uniqueid || item.pk,
                  nev: item.nev || item.name || item.nev_hu
                }));
              }
              const combined = [...accumulated, ...data];
              if (response.data.next) {
                return fetchAllKozsegek(page + 1, combined);
              }
              return combined;
            };
            const kozsegData = await fetchAllKozsegek();
            const uniqueKozsegek = Array.from(new Map(kozsegData.map(item => [item.id || item.Id || item.uniqueid || item.pk, item])).values());
            setKozsegOptions(uniqueKozsegek.sort((a, b) => (a.nev || '').localeCompare(b.nev || '')));
          } else {
            // Megkeressük a szék objektumot a név kinyeréséhez, ami megbízhatóbb a dropdown végpontnál
            const selectedSzekObj = szekOptions.find(s => s.nev === szekFilter || s.name === szekFilter || String(s.id) === String(szekFilter));
            const szekName = selectedSzekObj ? (selectedSzekObj.nev || selectedSzekObj.name) : szekFilter;

            const response = await Axios.get(`${config[environment].apiEndpoint}api/kozsegvaros-dropdown/?szek_nev=${encodeURIComponent(szekName)}`);
            const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
            setKozsegOptions(data);
          }
        } catch (e) {
        }
      };
      fetchKozsegek();
    } else {
      setKozsegOptions([]);
    }
  }, [szekFilter, isCsango, config, environment, szekOptions]);

  const handleSzekChange = (value) => {
    setSzekFilter(value);
    setKozsegFilter('');
    if (value === 'csango' || value === 'Csángóföld') {
      setIsCsango(true);
    } else {
      setIsCsango(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const filteredLabourers = dayLabourers.filter(l => {
    const matchesSearch = l.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (l.description && l.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = categoryFilter
      ? (l.category && l.category.toLowerCase().includes(categoryFilter.toLowerCase())) ||
        (l.description && l.description.toLowerCase().includes(categoryFilter.toLowerCase()))
      : true;
    
    const isRec = !!(l.is_recommended === true || l.is_recommended === 1 || l.is_recommended === '1' || l.is_recommended === 'true' || l.is_recommended === 'True');
    const matchesTab = tabValue === 1 ? isRec : !isRec;
    
    const matchesSzek = szekFilter ? (l.szek && l.szek.toLowerCase().includes(szekFilter.toLowerCase())) : true;
    const matchesKozseg = kozsegFilter ? (l.kozsegvaros && l.kozsegvaros.toLowerCase().includes(kozsegFilter.toLowerCase())) : true;

    return matchesSearch && matchesCategory && matchesTab && matchesSzek && matchesKozseg;
  });

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: '50px' }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" sx={{ color: 'var(--sznodarkblue)', fontWeight: 'bold' }}>
            {t('SubMenu.DayLabourers')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', md: 'auto' } }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate(`/${i18n.language}/napszamos-ajanlasa`)}
              sx={{ color: 'var(--sznodarkblue)', borderColor: 'var(--sznodarkblue)' }}
              fullWidth
            >
              {t('Business.RecommendDayLabourer', 'Napszámos ajánlása')}
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate(`/${i18n.language}/uzleti-regisztracio`)}
              sx={{ bgcolor: 'var(--sznodarkblue)' }}
              fullWidth
            >
              {t('Business.RegisterAsDayLabourer', 'Jelentkezés napszámosnak')}
            </Button>
          </Box>
          <TextField
            label={t('Common.Search', 'Keresés')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 250 }}
          />
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="day labourer tabs">
            <Tab label={`${t('Business.Registered')} (${dayLabourers.filter(l => !l.is_recommended).length})`} sx={{ fontWeight: 'bold' }} />
            <Tab 
              label={`${t('Business.Recommended')} (${dayLabourers.filter(l => l.is_recommended).length})`}
              sx={{ 
                fontWeight: 'bold',
                color: dayLabourers.some(l => l.is_recommended) ? 'var(--sznored)' : 'inherit'
              }} 
            />
          </Tabs>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <TextField
            select
            label={t('Events.Szek')}
            value={szekFilter}
            onChange={(e) => handleSzekChange(e.target.value)}
            sx={{ minWidth: 250 }}
            size="small"
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
          >
            <MenuItem value="">{t('Common.All')}</MenuItem>
            {szekOptions.map((sz, index) => (
              <MenuItem key={`${sz.id || sz.uniqueid || sz.pk || 'szek'}-${index}`} value={sz.nev || sz.name}>
                {sz.nev || sz.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={t('Events.Kozseg')}
            value={kozsegFilter}
            onChange={(e) => setKozsegFilter(e.target.value)}
            sx={{ minWidth: 250 }}
            size="small"
            disabled={!szekFilter && !isCsango}
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
          >
            <MenuItem value="">{t('Common.All')}</MenuItem>
            {kozsegOptions.map((k, index) => (
              <MenuItem key={`${k.id || k.uniqueid || k.pk || 'kozseg'}-${index}`} value={k.nev || k.name}>
                {k.nev || k.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={t('Common.Category', 'Kategória')}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{ minWidth: 250 }}
            size="small"
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
          >
            <MenuItem value="">{t('Common.All')}</MenuItem>
            {[...DayLabourerCategories]
              .map(cat => ({ ...cat, translatedLabel: cat.label.startsWith('Business.') ? t(cat.label, cat.label) : cat.label }))
              .sort((a, b) => a.translatedLabel.localeCompare(b.translatedLabel, i18n.language))
              .map(cat => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.translatedLabel}
                </MenuItem>
              ))}
          </TextField>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {filteredLabourers.length === 0 ? (
          <Typography variant="h6" sx={{ mt: 4, width: '100%', textAlign: 'center' }}>
            {t('Common.NoResults', 'Nincs találat.')}
          </Typography>
        ) : (
          filteredLabourers.map((labourer) => (
            <Grid item xs={12} sm={6} md={4} key={labourer.id}>
              <Card sx={{ height: '100%' }}>
                <CardActionArea onClick={() => navigate(`/${i18n.language}/napszamos/${labourer.id}`)}>
                  <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar 
                      src={labourer.picture || defaultProfilePicture} 
                      sx={{ width: 100, height: 100, mb: 2, border: '2px solid #105b79' }}
                    />
                    <Typography variant="h6" align="center" sx={{ fontWeight: 'bold' }}>
                      {labourer.name}
                    </Typography>
                    
                    {(labourer.category || labourer.description) && (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ fontWeight: 'bold', color: '#105b79', mt: 0.5 }}>
                        {labourer.category ? (labourer.category.startsWith('Business.') ? t(labourer.category) : labourer.category) : 
                         (labourer.description && labourer.description.length < 50 ? labourer.description : '')}
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {labourer.hourly_rate && (
                        <Chip label={`${labourer.hourly_rate} RON/óra`} size="small" variant="outlined" />
                      )}
                      {labourer.capacity > 1 && (
                        <Chip icon={<PeopleIcon />} label={`${labourer.capacity} fő`} size="small" variant="outlined" />
                      )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                      {labourer.description ? labourer.description.substring(0, 100) + '...' : ''}
                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Container>
  );
}

export default DayLabourers;